import React from 'react'
import PropTypes from 'prop-types'
import Transition from 'react-transition-group/Transition'
import { majorScale, withTheme, Pane, minorScale } from 'evergreen-ui'

const animationDuration = 300

const baseStyles = {
  zIndex: 1,
  position: 'absolute',
  top: 0,
  right: 0,
  transform: 'translateX(50%) translateY(-50%)',
  transitionDuration: `${animationDuration}ms`,
  transitionProperty: 'opacity, transform',
  borderRadius: 999,
  boxShadow: '0 0 0 1px white',
  overflow: 'hidden'
}

const textStyles = {
  height: 18,
  minWidth: 18,
  lineHeight: '18px',
  color: 'white',
  fontSize: 11,
  fontWeight: 600,
  textAlign: 'center',
  paddingX: minorScale(1)
}

const transitionStyles = {
  entering: {
    opacity: 0,
    transform: 'scale(0)',
    transitionTimingFunction: 'cubic-bezier(0.12, 0.4, 0.29, 1.46)'
  },
  entered: {
    opacity: 1,
    transform: 'scale(1)',
    transitionTimingFunction: 'cubic-bezier(0.12, 0.4, 0.29, 1.46)'
  },
  exiting: {
    opacity: 0,
    transform: 'scale(0)',
    transitionTimingFunction: 'cubic-bezier(0.71, -0.46, 0.88, 0.6)'
  }
}

function isZero(count) {
  return count === '0' || count === 0
}

function Dot(props) {
  return (
    <Pane
      is="sup"
      height={majorScale(1)}
      width={majorScale(1)}
      {...baseStyles}
      {...props}
    />
  )
}

function Count(props) {
  return <Pane is="sup" {...baseStyles} {...textStyles} {...props} />
}

class IconBadge extends React.PureComponent {
  static propTypes = {
    /** Optional styling for the actual badge/dot */
    badgeStyle: PropTypes.object,
    /** The content you want to anchor your badge to */
    children: PropTypes.node,
    /** The count to display in the badge */
    count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Whether or not to display as a dot (not count) */
    dot: PropTypes.bool,
    /** The max count to display before truncating to a `+` sign, e.g. `99+` */
    overflowCount: PropTypes.number,
    /** Whether or not zero counts should be displayed */
    showZero: PropTypes.bool,
    /** Evergreen's `withTheme` theme */
    theme: PropTypes.object
  }

  static defaultProps = {
    count: 0,
    dot: false,
    overflowCount: 99,
    showZero: false
  }

  static getDerivedStateFromProps(props, state) {
    const shown = props.showZero || !isZero(props.count)

    // keep the value the same as the previous state if exiting
    const count = shown ? props.count : state.count

    return { count, shown }
  }

  state = {
    count: this.props.count,
    shown: this.props.showZero || !isZero(this.props.count)
  }

  renderBadge() {
    const { badgeStyle, dot, overflowCount, theme } = this.props

    const { count } = this.state

    let displayCount = ''
    if (!dot) {
      displayCount =
        parseInt(count, 10) > parseInt(overflowCount, 10)
          ? `${overflowCount}+`
          : count
    }

    const Component = dot ? Dot : Count
    const props = {
      backgroundColor: theme.palette.red.base,
      style: badgeStyle
    }

    return (
      <Transition
        in={this.state.shown}
        appear
        unmountOnExit
        timeout={animationDuration}
      >
        {state => {
          const transitionStyle = transitionStyles[state] || {}
          return (
            <Component
              {...props}
              {...transitionStyle}
              pointerEvents="none"
              transform={`${baseStyles.transform} ${transitionStyle.transform ||
                ''}`}
            >
              {displayCount}
            </Component>
          )
        }}
      </Transition>
    )
  }

  render() {
    const {
      badgeStyle,
      children,
      count,
      dot,
      overflowCount,
      showZero,
      theme,
      ...props
    } = this.props

    return (
      <Pane position="relative" display="inline-flex" {...props}>
        {/* @todo animate this in/out */}
        {this.renderBadge()}
        {children}
      </Pane>
    )
  }
}

export default withTheme(IconBadge)
